import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Leaf, Gift } from "lucide-react";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";

const HERO = "https://images.pexels.com/photos/38444658/pexels-photo-38444658.jpeg?auto=compress&cs=tinysrgb&w=1400";
const ABOUT_IMG = "https://images.pexels.com/photos/14275717/pexels-photo-14275717.jpeg?auto=compress&cs=tinysrgb&w=1000";

const CATS = [
  { slug: "nuts", name: "Nuts", img: "https://images.pexels.com/photos/38571525/pexels-photo-38571525.jpeg?auto=compress&cs=tinysrgb&w=800", span: "md:col-span-2 md:row-span-2" },
  { slug: "dry-fruits", name: "Dry Fruits", img: "https://images.pexels.com/photos/18121300/pexels-photo-18121300.jpeg?auto=compress&cs=tinysrgb&w=800", span: "" },
  { slug: "gift-boxes", name: "Gift Boxes", img: "https://images.pexels.com/photos/20699854/pexels-photo-20699854.jpeg?auto=compress&cs=tinysrgb&w=800", span: "" },
  { slug: "berries", name: "Berries", img: "https://images.unsplash.com/photo-1587557757404-e521a89a16ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", span: "" },
  { slug: "exotic-collection", name: "Exotic Collection", img: "https://images.unsplash.com/photo-1695568181558-034b7d3e49eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", span: "" },
];

const Marquee = () => (
  <div className="marquee bg-forest text-cream py-4 border-y border-gold/20">
    <div className="marquee__track">
      {[...Array(2)].map((_, k) => (
        <span key={k} className="font-serif text-lg tracking-[0.25em] uppercase">
          {["Premium Quality", "Handpicked in India", "Luxury Gifting", "Local Delivery in Sitapur"].map((t) => (
            <span key={t} className="mx-12 text-gold/90">{t} <span className="text-gold mx-6">•</span></span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const HeroCarousel = () => {
  const [imgs, setImgs] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/products").then((r) => {
      const picks = r.data.filter((p) => p.images && p.images[0]).slice(0, 6);
      setImgs(picks.map((p) => ({ src: p.images[0], name: p.name, slug: p.slug })));
    });
  }, []);

  useEffect(() => {
    if (imgs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 3500);
    return () => clearInterval(t);
  }, [imgs]);

  const current = imgs[idx];

  return (
    <div className="relative" data-testid="hero-carousel">
      <div className="absolute -inset-4 bg-sand rounded-[2rem] -z-0" />
      <div className="relative rounded-[2rem] w-full h-[420px] lg:h-[560px] overflow-hidden bg-sand">
        {imgs.length === 0 && <img src={HERO} alt="Aadvan premium dry fruits" className="w-full h-full object-cover" />}
        {imgs.map((im, i) => (
          <img
            key={im.slug}
            src={im.src}
            alt={im.name}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === idx ? 1 : 0 }}
          />
        ))}
      </div>
      {current && (
        <Link
          to={`/product/${current.slug}`}
          data-testid="hero-featured-card"
          className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md rounded-xl px-5 py-3 border border-white hover:bg-white transition-colors"
        >
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Featured</div>
          <div className="font-serif text-lg text-forest">{current.name}</div>
        </Link>
      )}
      {imgs.length > 1 && (
        <div className="absolute bottom-6 right-6 flex gap-1.5">
          {imgs.map((im, i) => (
            <button
              key={im.slug}
              onClick={() => setIdx(i)}
              aria-label={`Show ${im.name}`}
              data-testid={`hero-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-gold" : "w-2 bg-white/70 hover:bg-white"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Home() {
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { best_seller: true } }).then((r) => setBestSellers(r.data.slice(0, 4)));
  }, []);

  return (
    <div className="fade-in">
      <Seo />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-12 lg:pt-20 pb-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-gold mb-6">Sitapur's Finest Since Day One</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-forest tracking-tight">
            Nuts & Dry Fruits,<br /><span className="italic text-gold">Perfected.</span>
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">
            Handpicked, freshly sourced and beautifully packed. Experience premium dry fruits and luxury gifting,
            delivered across Sitapur.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/products">
              <Button data-testid="hero-shop-button" className="bg-gold text-forest hover:bg-gold-soft rounded-full h-12 px-8 text-sm uppercase tracking-[0.15em]">
                Shop Collection <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/category/gift-boxes">
              <Button variant="outline" data-testid="hero-gift-button" className="border-forest text-forest hover:bg-forest hover:text-cream rounded-full h-12 px-8 text-sm uppercase tracking-[0.15em]">
                Explore Gifting
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex gap-8">
            <div><div className="font-serif text-3xl text-forest">100%</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Natural</div></div>
            <div><div className="font-serif text-3xl text-forest">15 KM</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Fast Delivery</div></div>
            <div><div className="font-serif text-3xl text-forest">30+</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Premium SKUs</div></div>
          </div>
        </div>
        <HeroCarousel />
      </section>

      <Marquee />

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          { icon: Leaf, t: "Farm Fresh", d: "Sourced at peak season" },
          { icon: ShieldCheck, t: "Lab Tested", d: "Purity guaranteed" },
          { icon: Truck, t: "Same-Day Delivery", d: "Across Sitapur" },
          { icon: Gift, t: "Luxury Gifting", d: "Curated hampers" },
        ].map((b) => (
          <div key={b.t} className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-sand flex items-center justify-center text-forest shrink-0"><b.icon size={20} /></div>
            <div><div className="font-serif text-lg text-forest leading-tight">{b.t}</div><div className="text-xs text-muted-foreground">{b.d}</div></div>
          </div>
        ))}
      </section>

      {/* Categories bento */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-4xl text-forest">Shop by Category</h2>
          <Link to="/products" className="text-sm text-gold hover:underline flex items-center gap-1">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[200px] gap-4">
          {CATS.map((c) => (
            <Link key={c.slug} to={`/category/${c.slug}`} data-testid={`home-cat-${c.slug}`}
              className={`group relative overflow-hidden rounded-2xl ${c.span}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/10 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="font-serif text-2xl text-cream">{c.name}</div>
                <div className="text-xs text-gold uppercase tracking-widest flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight size={12} /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Best sellers */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-gold">Loved by Sitapur</span>
            <h2 className="font-serif text-4xl text-forest mt-1">Our Best Sellers</h2>
          </div>
          <Link to="/products" className="text-sm text-gold hover:underline flex items-center gap-1">Shop All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* About strip */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-2 gap-12 items-center">
        <img src={ABOUT_IMG} alt="Aadvan luxury gifting" className="rounded-[2rem] w-full h-[400px] object-cover" />
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">The Aadvan Promise</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-forest mt-2 leading-tight">Purity you can taste, luxury you can gift.</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            From our 250 sq.ft premium store in Sitapur to your home, every batch is handpicked, quality-checked and
            packed fresh. We blend old-world trust with modern convenience — so you always get the very best.
          </p>
          <Link to="/about"><Button variant="outline" className="mt-6 border-forest text-forest hover:bg-forest hover:text-cream rounded-full h-12 px-8 text-sm uppercase tracking-[0.15em]" data-testid="home-about-button">Our Story</Button></Link>
        </div>
      </section>
    </div>
  );
}
